import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

/**
 * Manages the change scheduling form on ticket detail.
 * Handles real-time conflict detection when dates change.
 */
export default class extends Controller {
  static targets = [
    "startAt",
    "endAt",
    "category",
    "maintenanceWindow",
    "conflictList",
    "submitBtn",
  ]
  static values = {
    ticketId: String,
    scheduleUrl: String,
  }

  connect() {
    this.conflicts = []
  }

  async checkConflicts() {
    const startAt = this.startAtTarget.value
    const endAt = this.endAtTarget.value
    const category = this.categoryTarget.value

    if (!startAt || !endAt) {
      this.clearConflicts()
      return
    }

    try {
      const response = await patch(this.scheduleUrlValue, {
        body: JSON.stringify({
          support_ticket: {
            scheduled_start_at: startAt,
            scheduled_end_at: endAt,
            change_category: category,
            maintenance_window: this.maintenanceWindowTarget.checked,
          },
          dry_run: true,
        }),
        responseKind: "json",
      })

      const data = await response.json

      if (data.conflicts) {
        this.renderConflicts(data.conflicts)
      }
    } catch (e) {
      console.error("Conflict check failed:", e)
    }
  }

  renderConflicts(conflicts) {
    if (!this.hasConflictListTarget) return

    this.conflicts = conflicts

    if (conflicts.length === 0) {
      this.conflictListTarget.innerHTML = ""
      this.conflictListTarget.classList.add("hidden")
      return
    }

    this.conflictListTarget.classList.remove("hidden")
    this.conflictListTarget.innerHTML = ""

    for (const conflict of conflicts) {
      const isBlocking = conflict.severity === "blocking"
      const colorClass = isBlocking
        ? "bg-destructive/10 text-destructive border-destructive/20"
        : "bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300"

      const el = document.createElement("div")
      el.className = `flex items-start gap-2 p-2 rounded-md border text-xs ${colorClass}`

      const iconSpan = document.createElement("span")
      iconSpan.className = "shrink-0 mt-0.5"
      iconSpan.textContent = isBlocking ? "\u{1F6AB}" : "\u26A0"
      el.appendChild(iconSpan)

      const msgSpan = document.createElement("span")
      msgSpan.textContent = conflict.message
      el.appendChild(msgSpan)

      this.conflictListTarget.appendChild(el)
    }
  }

  clearConflicts() {
    if (!this.hasConflictListTarget) return
    this.conflictListTarget.innerHTML = ""
    this.conflictListTarget.classList.add("hidden")
    this.conflicts = []
  }

  async submit(event) {
    event.preventDefault()

    const hasBlocking = this.conflicts.some((c) => c.severity === "blocking")
    const category = this.categoryTarget.value

    if (hasBlocking && category !== "emergency") {
      return
    }

    this.submitBtnTarget.disabled = true

    try {
      const response = await patch(this.scheduleUrlValue, {
        body: JSON.stringify({
          support_ticket: {
            scheduled_start_at: this.startAtTarget.value,
            scheduled_end_at: this.endAtTarget.value,
            change_category: this.categoryTarget.value,
            maintenance_window: this.maintenanceWindowTarget.checked,
          },
        }),
        responseKind: "json",
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const data = await response.json
        if (data.conflicts) {
          this.renderConflicts(data.conflicts)
        }
      }
    } catch (e) {
      console.error("Schedule change failed:", e)
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }
}
